import { Callout } from '@/components/Callout/Callout';
import { Spinner } from '@/components/Spinner/Spinner';

import styles from './IdentityCard.module.css';

export interface IdentityCardProps {
  /** Who Turnstile says you are. */
  email: string | null;
  /** What Keystone answered for `me { id }` — the round trip this page exists
   *  to prove. Undefined while it is still in flight. */
  keystonePersonId?: string;
  loading?: boolean;
  error?: string | null;
}

type HopState = 'ok' | 'pending' | 'failed' | 'empty';

function Status({ state }: { state: HopState }) {
  if (state === 'pending') {
    return (
      <span className={`${styles.status} ${styles.pending}`}>
        <Spinner />
      </span>
    );
  }
  return (
    <span className={`${styles.status} ${styles[state]}`} aria-hidden="true">
      {state === 'ok' ? '✓' : state === 'failed' ? '!' : '–'}
    </span>
  );
}

export function IdentityCard({
  email,
  keystonePersonId,
  loading = false,
  error = null,
}: IdentityCardProps) {
  const turnstile: HopState = email ? 'ok' : 'empty';
  const keystone: HopState = loading
    ? 'pending'
    : error
      ? 'failed'
      : keystonePersonId
        ? 'ok'
        : 'empty';
  const allGood = turnstile === 'ok' && keystone === 'ok';

  return (
    <section className={styles.card}>
      <div className={styles.intro}>
        <span className={`${styles.eyebrow} ${allGood ? styles.eyebrowGood : ''}`}>
          {allGood ? 'All systems go' : 'Round trip'}
        </span>
        <h2 className={styles.heading}>Hello world</h2>
        <p className={styles.lede}>
          The portal signed you in with Turnstile and asked Keystone who you are. Both hops working
          is the whole of this page.
        </p>
      </div>

      <dl className={styles.rows}>
        <div className={styles.row}>
          <Status state={turnstile} />
          <div className={styles.rowText}>
            <dt>Turnstile identity</dt>
            <dd>{email ?? <span className={styles.muted}>no email claim</span>}</dd>
          </div>
        </div>
        <div className={styles.row}>
          <Status state={keystone} />
          <div className={styles.rowText}>
            <dt>Keystone person id</dt>
            <dd>
              {loading ? (
                <span className={styles.muted}>asking Keystone…</span>
              ) : keystonePersonId ? (
                <code>{keystonePersonId}</code>
              ) : (
                <span className={styles.muted}>—</span>
              )}
            </dd>
          </div>
        </div>
      </dl>

      {error ? (
        <Callout tone="danger" title="Keystone request failed">
          {error}
        </Callout>
      ) : null}
    </section>
  );
}
